import CitySEOHead from "@/components/feature/CitySEOHead";
import { useState } from "react";
import GulfportHeroSection from "./components/HeroSection";
import GulfportStickyNav from "./components/StickyNav";
import GulfportProcessingTimeSection from "./components/ProcessingTimeSection";
import GulfportFAQSection, { faqs } from "./components/FAQSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const destinations = [
  { name: "Caribbean Cruises", country: "Various", flag: "🚢", desc: "New Orleans and Mobile cruise ports. Gulfport is one of the most convenient Coast cities for cruises." },
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Top direct-flight beach destination from the Gulf Coast. Popular for spring break." },
  { name: "Germany", country: "Germany", flag: "🇩🇪", desc: "Keesler AFB personnel frequently assigned to Ramstein Air Base and European installations." },
  { name: "Japan", country: "Japan", flag: "🇯🇵", desc: "Yokota and Misawa assignments for Keesler personnel. Kadena AFB assignments also common." },
  { name: "Costa Rica", country: "Costa Rica", flag: "🇨🇷", desc: "Gulf Coast ecotourism and adventure travel. Growing destination for coastal Mississippi families." },
  { name: "Bahamas", country: "Bahamas", flag: "🇧🇸", desc: "Popular weekend cruise destination from Mobile and New Orleans for Gulfport families." },
];

const facilities = [
  { name: "Gulfport Main Post Office", address: "2711 28th St, Gulfport, MS 39501", phone: "(228) 863-7931", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "15–35 min", website: "https://tools.usps.com/find-location.htm?location=1378389", websiteLabel: "USPS Location" },
  { name: "Harrison County Chancery Clerk", address: "1801 23rd Ave, Gulfport, MS 39501", phone: "(228) 865-4131", hours: "Mon–Fri 8:00 AM – 4:30 PM", wait: "10–25 min", website: "https://harrisoncountyms.gov/departments/chancery-clerk", websiteLabel: "County Website" },
  { name: "Biloxi Post Office", address: "135 Main St, Biloxi, MS 39530", phone: "(228) 374-4010", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "10–20 min", website: "https://tools.usps.com/find-location.htm?location=1378384", websiteLabel: "USPS Location" },
];

const testimonials = [
  { name: "Airman 1st Class Tyler M.", city: "Keesler AFB, Gulfport", stars: 5, text: "Emergency orders to Japan with 3 days notice. Fast Passport Center delivered my passport in 48 hours. Absolute lifesaver." },
  { name: "Maria G.", city: "Long Beach, MS", stars: 5, text: "Getting a cruise ship job requires a passport fast. They had mine processed in 4 days. Highly recommend for Gulf Coast job seekers." },
  { name: "The Harrison Family", city: "D'Iberville", stars: 5, text: "Caribbean cruise with the whole family — 5 passports needed. All done in 5 days. Incredible service." },
];

const services = [
  { icon: "ri-flashlight-line", title: "Emergency Passport", desc: "24-hour processing for Keesler AFB deployment orders, short-notice PCS moves, and urgent civilian travel from Gulfport and the Mississippi Gulf Coast.", bullets: ["As fast as 24 hours", "Keesler AFB deployments welcome", "Same-day available"], badge: "Same-Day Available", link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport" },
  { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for military and civilian travelers on the Gulf Coast. DS-82 form assistance with overnight shipping from Gulfport.", bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"], badge: "Most Common", link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal" },
  { icon: "ri-passport-line", title: "New Passport", desc: "First-time applications for Gulf Coast residents. Complete DS-11 guidance, acceptance agent coordination at Gulfport facilities, and document prep.", bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=new-passport" },
  { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Emergency replacement with DS-64 form guidance. Quick reissue for Gulfport cruise passengers and military families facing sudden travel needs.", bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport" },
  { icon: "ri-parent-line", title: "Child Passport", desc: "Complete guidance for minors under 16. Both-parent consent coordination and acceptance clerk support for Gulf Coast families.", bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=child-passport" },
  { icon: "ri-edit-line", title: "Name Change", desc: "Post-marriage or legal name change updates. DS-5504 or DS-82 guidance with required documentation for Gulf Coast residents.", bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=name-change" },
  { icon: "ri-stack-line", title: "Second Passport", desc: "Need a second passport for frequent international travel? Ideal for Keesler AFB personnel with deployment needs and Gulf Coast maritime professionals.", bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=second-passport" },
  { icon: "ri-first-aid-kit-line", title: "Damaged Passport", desc: "Replacing a significantly damaged passport is faster than you think. A common need for Gulf Coast residents after hurricane water damage or flooding.", bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport" },
  { icon: "ri-bank-card-line", title: "Passport Card", desc: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. Perfect for Gulfport cruise passengers.", bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=passport-card" },
];

const timingData = [
  { month: "Year-Round", title: "Military Deployments", desc: "Keesler AFB personnel and families need passports on unpredictable military timelines year-round." },
  { month: "February–April", title: "Spring Break & Cruise Season", desc: "New Orleans and Mobile cruise departures peak. Cancun spring break flights surge." },
  { month: "May–June", title: "Summer Travel", desc: "Caribbean and beach vacations for Gulf Coast families. Casino employee training travel." },
  { month: "July–August", title: "Mission Trips", desc: "Church and hospital mission teams depart for Caribbean and Latin America." },
  { month: "September–October", title: "Off-Season Renewal", desc: "Ideal time to renew before the holiday cruise season." },
  { month: "November–December", title: "Holiday Cruises", desc: "New Orleans Christmas cruises and winter Caribbean departures. Rush orders spike." },
];

const comparisonRows = [
  { feature: "Processing Speed", us: "As fast as 24 hours", them: "10–13 weeks standard" },
  { feature: "Military Compatibility", us: "Full support for PCS and deployment orders", them: "No military-specific support" },
  { feature: "Office Visit Required", us: "No — mail or drop-off", them: "Yes — in-person required" },
  { feature: "Application Review", us: "Expert review before submission", them: "No review — errors cause delays" },
  { feature: "Tracking", us: "Real-time status updates", them: "Limited status info" },
  { feature: "Customer Support", us: "Phone + email, 7 days/week", them: "Phone only, limited hours" },
  { feature: "Price Guarantee", us: "Best price guarantee", them: "No price matching" },
  { feature: "Refund Policy", us: "Satisfaction guarantee", them: "No refunds after submission" },
  { feature: "Local Expertise", us: "Keesler AFB & Gulf Coast guidance", them: "Generic process only" },
];

const mistakes = [
  { title: "Military Timeline Mismatch", desc: "PCS and deployment orders arrive on short notice. Renew your passport before orders arrive — not after." },
  { title: "Cruise Boarding Day Surprise", desc: "Showing up at New Orleans or Mobile with an expired passport means missing your ship. We prevent this." },
  { title: "Wrong Photo", desc: "Photos from Gulf Coast CVS and Walgreens stores are commonly rejected. We verify photo specs in advance." },
  { title: "Late Application Start", desc: "Many Gulf Coast residents start passport processing 3 weeks before departure. We recommend starting 6–8 weeks early." },
  { title: "Form Confusion", desc: "DS-11 vs DS-82 is a common source of errors. We identify the correct form immediately." },
];

const howItWorks = [
  { num: "01", title: "Choose Your Service", desc: "Emergency, expedited, or standard — matched to your departure date." },
  { num: "02", title: "Complete Application", desc: "DS-11 or DS-82 — we walk you through every field." },
  { num: "03", title: "Visit Acceptance Facility", desc: "We direct you to the Gulfport facility with the shortest wait time." },
  { num: "04", title: "We Handle Processing", desc: "Hand-carried to the State Department for priority processing." },
  { num: "05", title: "Receive Your Passport", desc: "Secure delivery with real-time tracking." },
];

const servicesList = [
  { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
  { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
  { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
  { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
  { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
  { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
];

export default function GulfportPage() {
  return (
    <>
      <CitySEOHead
        city="Gulfport"
        state="Mississippi"
        stateAbbr="MS"
        route="/cities/gulfport"
        title="Emergency Passport Gulfport, MS — Expedited & Fast Passport Service | Fast Passport Center"
        description="Emergency and expedited passport services in Gulfport, MS — as fast as 24 hours. Serving Keesler AFB military, Gulf Coast families, and cruise travelers. A+ BBB rated, registered State Dept. courier."
        geoPlacename="Gulfport, Mississippi"
        geoPosition="30.3674;-89.0928"
        faqs={faqs}
        servicesSchema={servicesList}
        statePageName="Mississippi"
        statePageRoute="/states/mississippi"
      />
      <div className="font-sans">
      <GulfportHeroSection />
      <GulfportStickyNav />
      <GulfportProcessingTimeSection />

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Gulfport &amp; the Gulf Coast</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <div key={i} className="group relative bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col">
                {s.badge && (
                  <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                    {s.badge}
                  </span>
                )}
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${s.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{s.desc}</p>
                <ul className="space-y-1.5 mb-6 flex-1">
                  {s.bullets.map((b: string, j: number) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href={s.link}
                  className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap"
                >
                  Get Started <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area Section */}
      <section id="service-area" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Gulfport, Mississippi</h2>
          </div>
          <div className="max-w-3xl mx-auto text-gray-600 text-base leading-relaxed space-y-5">
            <p>Gulfport, Mississippi's second-largest city and the heart of the Mississippi Gulf Coast, generates significant and diverse international passport demand. Situated along the Gulf of Mexico with Biloxi to the east and Long Beach to the west, the Gulfport-Biloxi metropolitan area serves a population of over 400,000 and functions as a military, tourism, healthcare, and gaming hub for the entire region.</p>
            <p>Keesler Air Force Base in adjacent Biloxi is the most significant single driver of passport demand on the Gulf Coast. Home to the 81st Training Wing and a major Air Force medical center, Keesler processes thousands of active duty personnel and their families annually for PCS moves, deployments, and TDY assignments worldwide. The 403rd Wing, an Air Force Reserve Command unit at Keesler, adds hundreds of reservists with international military travel needs.</p>
            <p>The Gulf Coast tourism and gaming economy also generates substantial passport demand. Casino resort employees, particularly those in management roles with international parent companies, travel to Europe and Asia for training and conferences. The Port of Gulfport handles international cargo, with maritime professionals regularly requiring passports. Memorial Hospital and Garden Park Medical Center serve healthcare workers with medical conference and mission trip travel needs.</p>
            <p>Gulfport residents frequently use the New Orleans cruise port — just 90 minutes west — for Caribbean and Mexico cruises, and the Port of Mobile — just 90 minutes east — for additional cruise options. Spring break, summer vacation, and holiday cruise season all generate passport demand peaks that require faster processing than the standard government timeline provides.</p>
          </div>
        </div>
      </section>

      {/* Travel Timing Section */}
      <section id="travel-timing" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">When Gulfport Travelers Need Passports</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {timingData.map((t, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-wider mb-2">{t.month}</p>
                <h3 className="font-bold text-[#1A365D] text-lg mb-2">{t.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section id="destinations" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Gulfport Travelers Go</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {destinations.map((d, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{d.flag}</span>
                  <div><h3 className="font-bold text-[#1A365D] text-lg">{d.name}</h3><p className="text-gray-400 text-xs">{d.country}</p></div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Gulfport Travelers Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">{[...Array(t.stars)].map((_, j) => <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#EEF2F8] flex items-center justify-center"><i className="ri-user-line text-[#1A365D]"></i></div>
                  <div><p className="font-semibold text-[#1A365D] text-sm">{t.name}</p><p className="text-gray-400 text-xs">{t.city}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptance Facilities Section */}
      <AcceptanceFacilitiesSection facilities={facilities} />

      {/* Comparison Section */}
      {/* Comparison Section */}
      <div className="bg-[#FFF9E6] border border-[#F6C344]/20 rounded-lg p-6 mb-8 max-w-4xl mx-auto">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-[#1A365D] text-sm"></i>
          </div>
          <div>
            <p className="text-sm text-[#1A365D] font-semibold mb-1">Passport Agency Appointment Not Available?</p>
            <p className="text-sm text-gray-600">The nearest Regional Passport Agency is hours away with limited appointments. Skip the drive entirely. <a href="https://fastpassportcenter.com/checkout/" className="text-[#1A365D] font-semibold hover:underline whitespace-nowrap">Get Started <i className="ri-arrow-right-line"></i></a></p>
          </div>
        </div>
      </div>
      <ComparisonSection rows={comparisonRows} />

      {/* Mistakes Section */}
      <MistakesSection mistakes={mistakes} />

      {/* How It Works Section */}
      <HowItWorksSection steps={howItWorks} />

      <GulfportFAQSection />

      {/* Helpful Resources Section */}
      <section id="local-resources" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Gulfport Travelers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Travel.State.Gov", desc: "Official U.S. Department of State passport information.", link: "https://travel.state.gov/content/travel/en/passports.html" },
              { title: "Gulfport-Biloxi Airport", desc: "GPT airport flight information and international connections.", link: "https://www.flygpt.com/" },
              { title: "Keesler AFB", desc: "81st Training Wing military travel resources.", link: "https://www.keesler.af.mil/" },
            ].map((r, i) => (
              <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all group">
                <h3 className="font-bold text-[#1A365D] text-lg mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-3">{r.desc}</p>
                <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-semibold group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Vital Records Section */}
      <section id="vital-records" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Vital Records</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Birth, Death, Marriage &amp; Divorce Records in Harrison County</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Need a certified copy of a birth certificate, death certificate, marriage license, or divorce decree? Here are the official resources for Harrison County and the Mississippi Gulf Coast.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <a href="https://msdh.ms.gov/msdhsite/_static/31,0,109.html" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className="ri-government-line text-[#1A365D] text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Mississippi State Department of Health — Vital Records</h3>
              <p className="text-gray-500 text-xs">Official source for Mississippi birth certificates (1912–present) and death certificates (1912–present). Online, mail, and in-person ordering from the Jackson office. Also handles marriage and divorce verification.</p>
            </a>
            <a href="https://harrisoncountyms.gov/departments/chancery-clerk" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className="ri-building-line text-[#1A365D] text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Harrison County Chancery Clerk</h3>
              <p className="text-gray-500 text-xs">Marriage licenses issued in Harrison County are maintained by the Chancery Clerk at 1801 23rd Ave, Gulfport. Contact for certified copies of marriage records for Gulfport, Biloxi, and all Harrison County communities.</p>
            </a>
            <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className="ri-global-line text-[#1A365D] text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">VitalChek — Online Ordering</h3>
              <p className="text-gray-500 text-xs">Order certified Mississippi birth and death certificates online through VitalChek, the authorized vendor for MSDH. Expedited shipping available for Gulfport and Gulf Coast residents.</p>
            </a>
          </div>
        </div>
      </section>

      <SEOCrossLinks currentRoute="/cities/gulfport" state="MS" />

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#1A365D]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport in Gulfport?</h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">Military, civilian, or cruise traveler — we have the speed and expertise to get your passport when you need it.</p>
          <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Passport <i className="ri-arrow-right-line"></i></a>
          <p className="text-white/50 text-sm mt-6">Call us at <a href="tel:8772530084" className="text-[#F6C344] hover:underline">877-253-0084</a> for immediate assistance</p>
        </div>
      </section>
      </div>
    </>
  );
}

function AcceptanceFacilitiesSection({ facilities }: { facilities: { name: string; address: string; phone: string; hours: string; wait: string; website: string; websiteLabel: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="acceptance-facilities" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Acceptance Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Gulfport</h2>
        </div>
        <div className="space-y-4 max-w-3xl mx-auto">
          {facilities.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-map-pin-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><p className="text-gray-400 text-xs">{f.address}</p></div>
                </div>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl`}></i>
              </button>
              {open === i && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-4">
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Phone</p><p className="text-[#1A365D] text-sm font-medium">{f.phone}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Hours</p><p className="text-[#1A365D] text-sm font-medium">{f.hours}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Avg Wait</p><p className="text-[#1A365D] text-sm font-medium">{f.wait}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Website</p><a href={f.website} target="_blank" rel="nofollow noopener noreferrer" className="text-[#1A365D] hover:text-[#F6C344] text-sm font-medium transition-colors inline-flex items-center gap-1"><i className="ri-global-line"></i> {f.websiteLabel}</a></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection({ rows }: { rows: { feature: string; us: string; them: string }[] }) {
  return (
    <section id="comparison" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. DIY Government Processing</h2>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 border-b border-gray-200 bg-[#EEF2F8]">
            <div className="p-4 font-bold text-[#1A365D] text-sm">Feature</div>
            <div className="p-4 font-bold text-[#1A365D] text-sm text-center">Fast Passport Center</div>
            <div className="p-4 font-bold text-gray-500 text-sm text-center">Government Standard</div>
          </div>
          {rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 ${i !== rows.length - 1 ? "border-b border-gray-100" : ""}`}>
              <div className="p-4 text-sm text-gray-600 font-medium">{row.feature}</div>
              <div className="p-4 text-sm text-[#1A365D] font-semibold text-center bg-[#F7F8FA]/50">{row.us}</div>
              <div className="p-4 text-sm text-gray-500 text-center">{row.them}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MistakesSection({ mistakes }: { mistakes: { title: string; desc: string }[] }) {
  return (
    <section id="mistakes" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Mistakes Gulfport Applicants Make</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-4"><i className="ri-close-circle-line text-red-500 text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{m.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection({ steps }: { steps: { num: string; title: string; desc: string }[] }) {
  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">How It Works</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Steps to Your Passport</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-lg mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}